import { appState } from '../core/stateManager.js';
import { FileParser } from '../services/fileParser.js';
import { aiService } from '../services/aiService.js';
import { naverBlogService } from '../services/naverBlogService.js';
import { getAdminState } from './admin.js';
import { updateProjectDropdown } from './navigation.js';
import { renderDashboard } from './proposal.js';

let quickDocToastEditor = null;
let quickDocEditorLang = null;
let quickDocEditorSaveTimer = null;

function escapeHtml(text = '') {
    return String(text || '').replace(/[&<>"']/g, ch => ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;'
    }[ch]));
}

function getTranscriptFailureCopy(error = '') {
    const code = String(error || 'unknown_error');
    const known = {
        transcript_not_available: '이 영상에서 공개 자막 또는 자동 생성 자막을 가져오지 못했습니다.',
        youtube_watch_429: 'YouTube가 현재 대사 추출 요청을 제한했습니다. 잠시 후 재시도하거나 직접 대본/요약을 넣어야 합니다.',
        transcript_http_404: '대사 추출 API 경로 또는 배포 설정을 확인해야 합니다.',
        transcript_request_failed: '대사 추출 서비스 호출이 완료되지 않았습니다.'
    };
    return known[code] || `대사 추출 서비스가 ${code} 상태를 반환했습니다.`;
}

function inferBlogCategoryFromTypes(types = []) {
    const joined = types.join(' ');
    if (/여행|맛집/.test(joined)) return '여행/지역탐방';
    if (/경험|일상/.test(joined)) return '일상/라이프/생각';
    if (/홍보|브랜딩|비즈니스/.test(joined)) return 'IT/테크/비즈니스';
    if (/리뷰|정보|테크/.test(joined)) return 'IT/테크/비즈니스';
    return 'IT/테크/비즈니스';
}

function detectQuickDocSourceKind(text = '') {
    const source = String(text || '').trim();
    if (/https?:\/\/(?:www\.)?(?:youtube\.com|youtu\.be)\S*/i.test(source)) {
        return 'youtube';
    }
    if (/https?:\/\/\S+/i.test(source)) {
        return 'url';
    }
    return 'memo';
}

function extractFirstUrl(text = '') {
    return String(text || '').match(/https?:\/\/\S+/i)?.[0] || '';
}

async function fetchYouTubeTranscriptContext(sourceUrl) {
    if (!sourceUrl) return { success: false, error: 'missing_youtube_url', message: 'YouTube URL이 없습니다.' };
    try {
        const response = await fetch(`/api/youtube-transcript?url=${encodeURIComponent(sourceUrl)}`);
        const data = await response.json().catch(() => ({}));
        if (!response.ok || !data.success || !data.transcript) {
            return {
                success: false,
                error: data.error || `transcript_http_${response.status}`,
                videoId: data.videoId || '',
                title: data.title || '',
                message: 'YouTube 자막/대사를 자동 추출하지 못했습니다.'
            };
        }
        const titleLine = data.title ? `- 영상 제목: ${data.title}\n` : '';
        const langLine = data.language ? `- 자막 언어: ${data.language}${data.isAutoGenerated ? ' (자동 생성)' : ''}\n` : '';
        return {
            success: true,
            error: '',
            videoId: data.videoId || '',
            title: data.title || '',
            language: data.language || '',
            isAutoGenerated: Boolean(data.isAutoGenerated),
            transcript: data.transcript,
            preview: data.transcript.slice(0, 280),
            context: `[YouTube 대사 추출 결과]\n- URL: ${sourceUrl}\n${titleLine}${langLine}\n\n${data.transcript}`
        };
    } catch (err) {
        return {
            success: false,
            error: 'transcript_request_failed',
            message: `YouTube 대사 추출 요청 실패 (${err.message})`
        };
    }
}

function renderYouTubeTranscriptStatus(status = null) {
    const statusEl = document.getElementById('q-youtube-status');
    if (!statusEl) return;

    if (!status) {
        statusEl.className = 'hidden mt-3 rounded-xl border px-4 py-3 text-xs font-bold leading-relaxed';
        statusEl.textContent = '';
        return;
    }

    statusEl.classList.remove('hidden');
    if (status.success) {
        statusEl.className = 'mt-3 rounded-xl border px-4 py-3 text-xs leading-relaxed bg-emerald-50 border-emerald-200 text-emerald-800';
        const lang = status.language ? ` / ${status.language}${status.isAutoGenerated ? ' 자동 자막' : ''}` : '';
        const title = status.title ? `<div class="mt-1 font-black text-slate-800">영상: ${escapeHtml(status.title)}</div>` : '';
        const preview = status.preview ? `
            <details class="mt-3 rounded-lg bg-white/70 border border-emerald-100 px-3 py-2">
                <summary class="cursor-pointer font-black text-emerald-700">추출된 대사 미리보기</summary>
                <pre class="mt-2 whitespace-pre-wrap font-mono text-[11px] text-slate-700 max-h-40 overflow-auto">${escapeHtml(status.preview)}</pre>
            </details>` : '';
        statusEl.innerHTML = `
            <div class="font-black text-emerald-700">YouTube 대사 추출 완료${escapeHtml(lang)}</div>
            <div class="mt-1 font-bold">이 대사를 블로그 원문 맥락에 자동으로 추가했습니다.</div>
            ${title}
            ${preview}
        `;
        return;
    }

    const reason = getTranscriptFailureCopy(status.error);
    statusEl.className = 'mt-3 rounded-xl border px-4 py-3 text-xs leading-relaxed bg-amber-50 border-amber-200 text-amber-900';
    statusEl.innerHTML = `
        <div class="font-black text-amber-900">YouTube 대사 추출 실패</div>
        <div class="mt-1 font-bold">${escapeHtml(reason)}</div>
        <div class="mt-2 text-[11px] text-amber-800">
            코드: <span class="font-mono">${escapeHtml(status.error || 'unknown_error')}</span>
        </div>
        <div class="mt-3 rounded-lg bg-white/70 border border-amber-100 px-3 py-2 text-[11px] font-bold">
            URL만 입력한 상태에서는 영상 내용 기반 블로그를 만들지 않습니다. 영상 대본, 핵심 요약, 작성 소재를 왼쪽 입력칸에 추가하면 메모 기반으로 계속 진행합니다.
        </div>
    `;
}

function removeFirstUrl(text = '') {
    return String(text || '').replace(/https?:\/\/\S+/i, '').trim();
}

function normalizeBlogTags(tags) {
    if (Array.isArray(tags)) {
        return tags.map(tag => String(tag || '').replace(/^#/, '').trim()).filter(Boolean);
    }
    return String(tags || '')
        .split(/[,，#\n]/)
        .map(tag => tag.trim())
        .filter(Boolean);
}

function normalizeQuickDocGeneratedValue(value) {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
        const title = String(value.title || value.제목 || '').trim();
        const description = String(value.description || value.desc || value.설명 || '').trim();
        const tags = normalizeBlogTags(value.tags || value.tag || value.태그);
        const body = normalizeQuickDocGeneratedText(value.body || value.content || value.본문 || '');
        const parts = [];
        if (title && !body.trim().startsWith('#')) parts.push(`# ${title}`);
        if (description) parts.push(`> ${description}`);
        if (tags.length > 0) parts.push(`태그: ${tags.map(tag => `#${tag}`).join(' ')}`);
        if (body) parts.push(body);
        return normalizeQuickDocGeneratedText(parts.join('\n\n'));
    }
    return normalizeQuickDocGeneratedText(value);
}

export function extractQuickDocBlogMetadata(parsedResults = {}, targetLangs = []) {
    const orderedValues = [
        ...targetLangs.map(lang => parsedResults?.[lang]).filter(Boolean),
        ...Object.values(parsedResults || {}).filter(Boolean)
    ];
    const metadata = { title: '', description: '', tags: [] };

    for (const value of orderedValues) {
        if (value && typeof value === 'object' && !Array.isArray(value)) {
            metadata.title ||= String(value.title || value.제목 || '').trim();
            metadata.description ||= String(value.description || value.desc || value.설명 || '').trim();
            if (metadata.tags.length === 0) metadata.tags = normalizeBlogTags(value.tags || value.tag || value.태그);
        } else if (typeof value === 'string') {
            metadata.title ||= value.match(/^#\s+(.+)$/m)?.[1]?.trim() || '';
            metadata.description ||= value.match(/^(?:설명|description)\s*[:：]\s*(.+)$/im)?.[1]?.trim() || '';
            if (metadata.tags.length === 0) {
                metadata.tags = normalizeBlogTags(value.match(/^(?:태그|tags?)\s*[:：]\s*(.+)$/im)?.[1] || '');
            }
        }
        if (metadata.title && metadata.description && metadata.tags.length > 0) break;
    }

    return metadata;
}

const safeMarkedParse = (text) => {
    if (typeof marked !== 'undefined') {
        if (typeof marked.parse === 'function') {
            return marked.parse(text || "");
        } else if (typeof marked === 'function') {
            return marked(text || "");
        }
    }
    return (text || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/\n/g, "<br>");
};

function getBlogMetadataFromMarkdown(text = '') {
    const source = String(text || '');
    const title = source.match(/^#\s+(.+)$/m)?.[1]?.trim() || '';
    const description = source.match(/^>\s+(.+)$/m)?.[1]?.trim()
        || source.match(/^(?:설명|description)\s*[:：]\s*(.+)$/im)?.[1]?.trim()
        || '';
    const tags = normalizeBlogTags(source.match(/^(?:태그|tags?)\s*[:：]\s*(.+)$/im)?.[1] || '');
    return { title, description, tags };
}

function renderQuickDocPreviewContent(text = '', lang = '') {
    if (!isQuickDocBlogProject()) return safeMarkedParse(text);

    const state = appState.getState();
    const fallback = getBlogMetadataFromMarkdown(text);
    const title = state.projectData?.quickDocBlogTitle || fallback.title;
    const description = state.projectData?.quickDocBlogDescription || fallback.description;
    const tags = state.projectData?.quickDocBlogGeneratedTags?.length
        ? state.projectData.quickDocBlogGeneratedTags
        : fallback.tags;
    const tagsHtml = tags.length
        ? `<div class="flex flex-wrap gap-2 mt-3">${tags.map(tag => `<span class="px-2.5 py-1 rounded-full bg-teal-50 text-teal-700 border border-teal-100 text-[11px] font-black">#${escapeHtml(tag)}</span>`).join('')}</div>`
        : '';
    const metaHtml = (title || description || tags.length)
        ? `<div class="mb-6 rounded-2xl border border-slate-200 bg-slate-50 p-5">
                <div class="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">블로그 결과 요약${lang ? ` / ${escapeHtml(lang)}` : ''}</div>
                ${title ? `<h2 class="text-xl font-black text-slate-900 leading-tight">${escapeHtml(title)}</h2>` : ''}
                ${description ? `<p class="mt-2 text-sm font-bold text-slate-600 leading-relaxed">${escapeHtml(description)}</p>` : ''}
                ${tagsHtml}
           </div>`
        : '';
    return `${metaHtml}<div class="quickdoc-blog-body">${safeMarkedParse(text)}</div>`;
}

function getActiveQuickDocLang() {
    return document.querySelector('#q-lang-tabs .q-lang-tab.active')?.dataset.lang || '';
}

function getQuickDocResultText(lang) {
    const state = appState.getState();
    const results = state.projectData?.quickDocResults || {};
    if (quickDocToastEditor && quickDocEditorLang === lang && !document.getElementById('q-editor-wrap')?.classList.contains('hidden')) {
        return quickDocToastEditor.getMarkdown();
    }
    return results[lang] || '';
}

function saveQuickDocEditorContent() {
    if (!quickDocToastEditor || !quickDocEditorLang) return;
    const state = appState.getState();
    const current = quickDocToastEditor.getMarkdown();
    state.projectData.quickDocResults = {
        ...(state.projectData.quickDocResults || {}),
        [quickDocEditorLang]: current
    };
    appState.saveCurrentProject();
}

function scheduleQuickDocEditorSave() {
    clearTimeout(quickDocEditorSaveTimer);
    quickDocEditorSaveTimer = setTimeout(saveQuickDocEditorContent, 400);
}

function isQuickDocBlogProject() {
    return appState.getState().projectData?.documentType === 'blog';
}

function setQuickDocEditMode(enabled, lang = getActiveQuickDocLang()) {
    const editorWrap = document.getElementById('q-editor-wrap');
    const editorHost = document.getElementById('q-toast-editor');
    const content = document.getElementById('q-rendered-content');
    const empty = document.getElementById('q-preview-empty');
    const editBtn = document.getElementById('btn-q-edit');
    if (!editorWrap || !editorHost || !content || !editBtn) return;

    if (!enabled) {
        saveQuickDocEditorContent();
        editorWrap.classList.add('hidden');
        content.classList.remove('hidden');
        editBtn.innerText = '✏️ 편집';
        const finalText = getQuickDocResultText(lang);
        if (finalText) content.innerHTML = renderQuickDocPreviewContent(finalText, lang);
        return;
    }

    const ToastEditor = window.toastui?.Editor;
    if (!ToastEditor) {
        alert('편집기 라이브러리를 아직 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.');
        return;
    }

    if (quickDocToastEditor && quickDocEditorLang !== lang) {
        saveQuickDocEditorContent();
    }

    const markdown = getQuickDocResultText(lang);
    content.classList.add('hidden');
    if (empty) empty.classList.add('hidden');
    editorWrap.classList.remove('hidden');
    editBtn.innerText = '👁️ 보기';

    if (!quickDocToastEditor) {
        quickDocToastEditor = new ToastEditor({
            el: editorHost,
            height: '100%',
            initialEditType: 'wysiwyg',
            previewStyle: 'vertical',
            initialValue: markdown,
            usageStatistics: false
        });
        quickDocToastEditor.on('change', scheduleQuickDocEditorSave);
    } else {
        quickDocToastEditor.setMarkdown(markdown || '', false);
    }
    quickDocEditorLang = lang;
}

function resetQuickDocEditor() {
    clearTimeout(quickDocEditorSaveTimer);
    if (quickDocToastEditor) {
        saveQuickDocEditorContent();
    }
    document.getElementById('q-editor-wrap')?.classList.add('hidden');
    const editBtn = document.getElementById('btn-q-edit');
    if (editBtn) editBtn.innerText = '✏️ 편집';
}

function updateStepIndicators(n, maxStep, pipeline) {
    const qBar = pipeline.querySelector('#q-progress-bar');
    if (qBar) qBar.style.width = n===1 ? '0%' : n===2 ? '50%' : '100%';
    
    const state = appState.getState();
    const docType = state.projectData?.documentType || 'meeting';
    const labelsMap = {
        'meeting': ['음성/글 입력', '핵심의사결정 요약', '회의록 메일 발송'],
        'email': ['수신처 & 톤설정', '핵심 요구사항 기술', '메일 초안 완성'],
        'blog': ['타겟 키워드', '본문 & 카피라이팅', 'SEO 태그 추출'],
        'experience': ['행선지/일정 수립', '체험 계획 기술', '신청서/보고서 인쇄'],
        'default': ['1단계: 설정', '2단계: 생성', '3단계: 완료']
    };
    const labels = labelsMap[docType] || labelsMap['meeting'];

    for(let i=1; i<=3; i++) {
        const cWrapper = pipeline.querySelector(`#btn-q-step-${i}`);
        const c = pipeline.querySelector(`#q-ind-${i}`);
        if (!c || !cWrapper) continue;
        
        const span = cWrapper.querySelector('span');
        if (span && labels[i - 1]) {
            span.innerText = labels[i - 1];
        }

        if (i > maxStep) {
            cWrapper.classList.add('cursor-not-allowed', 'opacity-50');
            cWrapper.classList.remove('group');
            c.className = "w-14 h-14 rounded-full flex justify-center items-center font-bold text-xl shadow-md border-4 border-white bg-slate-200 text-slate-400";
        } else {
            cWrapper.classList.remove('cursor-not-allowed', 'opacity-50');
            cWrapper.classList.add('group');
            if(i < n) {
                c.className = "w-14 h-14 rounded-full flex justify-center items-center font-bold text-xl shadow-md border-4 border-white bg-teal-600 text-white";
                c.innerHTML = "✓";
            }
            else if(i === n) {
                c.className = "w-14 h-14 rounded-full flex justify-center items-center font-bold text-xl shadow-md border-4 border-white bg-teal-600 text-white ring-4 ring-teal-100";
                c.innerHTML = i;
            }
            else {
                c.className = "w-14 h-14 rounded-full flex justify-center items-center font-bold text-xl shadow-md border-4 border-white bg-white text-slate-400 group-hover:scale-110 transition-transform cursor-pointer";
                c.innerHTML = i;
            }
        }
    }
}

export function qStep(n) {
    const state = appState.getState();
    if (state.activeMode !== 'quickdoc' && n !== 0) return;

    if (n > 0 && n > state.quickDocMaxStep) {
        return; // Prevent navigating beyond max step
    }

    appState.setState({ quickDocStep: n });
    window.scrollTo(0, 0);
    
    // Restore config UI if moving to configuration or result steps
    if (n >= 2) {
        setTimeout(restoreQuickDocConfig, 50);
    }
    
    const pipeline = document.getElementById('pipeline-quickdoc');
    if (!pipeline) return;
    document.getElementById('pipeline-proposal')?.classList.add('hidden');

    ['step-0', 'q-step-1', 'qd-view'].forEach(id => {
        const el = document.getElementById(id);
        if (el) el.classList.add('step-hidden');
    });
    
    const modeDesc = document.getElementById('mode-desc');
    const tDisp = document.getElementById('project-title-input');

    if(n === 0) { 
        const s0 = document.getElementById('step-0');
        if(s0) s0.classList.remove('step-hidden'); 
        const dView = document.getElementById('dashboard-view');
        if(dView) {
            dView.classList.remove('hidden');
        }
        if(tDisp) { 
            tDisp.value = "대시보드"; 
            tDisp.readOnly = true;
            tDisp.classList.remove('hover:bg-slate-50', 'cursor-pointer');
            tDisp.classList.add('cursor-default');
        }
        document.getElementById('project-title-edit-icon')?.classList.add('hidden');
        document.getElementById('btn-exit-project')?.classList.add('hidden');
        pipeline.classList.add('hidden');
        renderDashboard();
        return; 
    } else {
        document.getElementById('dashboard-view')?.classList.add('hidden');
        pipeline.classList.remove('hidden');
        if(tDisp) { 
            tDisp.value = state.projectData.name || "신규 빠른 문서"; 
            tDisp.readOnly = false;
            tDisp.classList.remove('cursor-default');
            tDisp.classList.add('hover:bg-slate-50', 'cursor-pointer');
        }
        document.getElementById('project-title-edit-icon')?.classList.remove('hidden');
        document.getElementById('btn-exit-project')?.classList.remove('hidden');
        if (modeDesc) {
            const docType = state.projectData.documentType || 'meeting';
            const typeNames = { 'meeting': '회의록', 'email': '이메일', 'blog': '블로그', 'experience': '체험학습 신청/보고서' };
            const typeName = typeNames[docType] || '빠른 문서';
            const stepNames = ["", "1단계: 맥락 및 데이터 입력", "2단계: 문서 세부 설정", "3단계: AI 다국어 렌더링"];
            modeDesc.innerText = `${typeName} 작성 - ${stepNames[n] || '진행'}`;
        }
    }
    
    ['q-step-1', 'qd-view'].forEach(id => {
        const el = document.getElementById(id);
        if (el) el.classList.add('step-hidden');
    });
    
    if (n === 1) {
        pipeline.querySelector('#q-step-1').classList.remove('step-hidden');
        const textArea = document.getElementById('q-text-input');
        const inputLabel = document.getElementById('q-input-label');
        const docType = state.projectData.documentType || 'meeting';

        const uploadZoneTitle = document.getElementById('q-upload-zone-title');
        const uploadHintTitle = document.getElementById('q-upload-hint-title');
        const uploadHintDesc = document.getElementById('q-upload-hint-desc');

        if (docType === 'blog') {
            if (uploadZoneTitle) uploadZoneTitle.innerText = "📷 본문 첨부용 사진 또는 참고 자료 업로드";
            if (uploadHintTitle) uploadHintTitle.innerText = "클릭하여 사진(JPG, PNG) 올리기";
            if (uploadHintDesc) uploadHintDesc.innerText = "YouTube URL은 위 입력창에 붙여 넣으면 대사를 자동 추출합니다";
        } else {
            if (uploadZoneTitle) uploadZoneTitle.innerText = "문서, 음성 또는 참고 사진(이미지) 업로드";
            if (uploadHintTitle) uploadHintTitle.innerText = "클릭하여 파일 또는 사진 올리기";
            if (uploadHintDesc) uploadHintDesc.innerText = "Audio, PDF, TXT, 이미지(JPG, PNG)";
        }

        const guidanceMap = {
            'meeting': {
                label: "📝 회의 내용 요약 또는 음성 파일 가이드",
                placeholder: "회의에서 오간 핵심 안건이나 결정사항을 간단히 적어주세요.",
                defaultValue: "회의 주요 결정사항: [여기에 핵심 의사결정 사항이나 메모를 적으세요 - 예: 2분기 예산 조율 완료 / 다음 주 디자인 시안 검토 예정 / 홍길동 주간 보고 22일 제출]"
            },
            'email': {
                label: "✉️ 비즈니스 이메일 수신 정보 및 용건 작성",
                placeholder: "누구에게 어떤 용건으로 메일을 쓰는지 적어주세요.",
                defaultValue: "수신: A사 영업마케팅팀 박 부장님\n목적: 이번 주 금요일 미팅 일정 변경 정중히 사과 및 재조율 요청\n추가 전달사항: 다음 주 월요일 또는 화요일 오후 시간대 제안하며 양해 구함"
            },
            'blog': {
                label: "📰 블로그 소스 입력 (YouTube URL, 웹 URL, 메모)",
                placeholder: "YouTube URL, 웹페이지 URL, 블로그 주제, 핵심 소재를 입력해 주세요.\n\n예)\nhttps://youtu.be/...\n또는\n블로그 주제: 강릉 경포대 해변 1박 2일 가족 여행기\n핵심 소재: 첫날 해돋이, 동해안 횟집, 모래사장 산책\n노출 키워드: 강릉 여행, 경포대 맛집",
                defaultValue: "블로그 주제: 강릉 경포대 해변 1박 2일 가족 여행기\n핵심 소재: 첫날 해돋이의 장관, 동해안의 신선한 횟집 스펙터클 맛 평가, 모래사장 산책 감상\n노출 키워드: 강릉 여행, 경포대 맛집"
            },
            'experience': {
                label: "🎒 현장체험학습 신청서 및 보고서 작성 정보",
                placeholder: "행선지, 대상 아동 정보, 학습 방향을 자유롭게 적어주세요.",
                defaultValue: "학습 아동 정보: 초등학교 5학년 김민지\n체험 행선지: 경주 불국사 및 석굴암 일대 역사 탐방 (총 3일간)\n주요 학습 방향: 신라 천년의 불교 문화재 직접 관찰, 문화재 보존의 가치와 역사적 의의 고찰"
            }
        };

        const guidance = guidanceMap[docType] || guidanceMap['meeting'];
        if (inputLabel) inputLabel.innerText = guidance.label;
        
        // 1. Restore clean text-input visibility
        if (textArea) {
            textArea.classList.remove('hidden');
            textArea.placeholder = guidance.placeholder;
            textArea.value = state.projectData.quickDocSource || "";
        }
        renderYouTubeTranscriptStatus(docType === 'blog' ? state.projectData.quickDocTranscriptStatus : null);

        // 2. Toggle Blog Types Panel
        const blogTypesPanel = document.getElementById('q-blog-types-panel');
        if (blogTypesPanel) {
            if (docType === 'blog') {
                blogTypesPanel.classList.remove('hidden');
            } else {
                blogTypesPanel.classList.add('hidden');
            }
        }
    } else if (n === 2 || n === 3) {
        pipeline.querySelector('#qd-view').classList.remove('step-hidden');
        
        // Sync dropdowns/checkboxes from projectData
        const docType = document.getElementById('q-doc-type');
        const docAudience = document.getElementById('q-doc-audience');
        const docTone = document.getElementById('q-doc-tone');
        
        if (docType) {
            const docTypeNameMap = {
                'meeting': '회의록',
                'email': '이메일',
                'blog': '블로그',
                'experience': '체험학습'
            };
            const targetVal = state.projectData.quickDocType || docTypeNameMap[state.projectData.documentType] || '회의록';
            docType.value = targetVal;
            docType.disabled = true;
            docType.classList.add('bg-slate-100', 'cursor-not-allowed', 'text-slate-500', 'opacity-80');
        }
        if (docAudience && state.projectData.quickDocAudience) {
            docAudience.value = state.projectData.quickDocAudience;
            if (state.projectData.quickDocAudience === 'custom' && state.projectData.quickDocCustomAudience) {
                document.getElementById('q-custom-audience-field')?.classList.remove('hidden');
                document.getElementById('q-custom-audience').value = state.projectData.quickDocCustomAudience;
            }
        }
        if (docTone && state.projectData.quickDocTone) docTone.value = state.projectData.quickDocTone;
        
        // Sync checkboxes
        if (state.projectData.quickDocLangs) {
            const checks = document.querySelectorAll('#q-lang-checkboxes input');
            checks.forEach(c => {
                c.checked = state.projectData.quickDocLangs.includes(c.value);
            });
        }

        // Show/Hide Naver Blog panel based on project documentType
        const blogPanel = document.getElementById('q-blog-publishing-panel');
        if (blogPanel) {
            if (state.projectData.documentType === 'blog') {
                blogPanel.classList.remove('hidden');
                const categoryInput = document.getElementById('q-blog-category');
                const categoryField = document.getElementById('q-blog-category-field');
                if (categoryInput && state.projectData.quickDocBlogCategory) {
                    categoryInput.value = state.projectData.quickDocBlogCategory;
                }
                if (categoryField) {
                    categoryField.classList.add('hidden');
                }
                const keywordInput = document.getElementById('q-blog-target-keywords');
                const keywordField = document.getElementById('q-blog-target-keywords-field');
                if (keywordInput && !keywordInput.value && state.projectData.quickDocBlogKeywords) {
                    keywordInput.value = state.projectData.quickDocBlogKeywords.join(', ');
                }
                if (keywordField) {
                    keywordField.classList.add('hidden');
                }
                updateNaverApiStatusUI();
            } else {
                blogPanel.classList.add('hidden');
            }
        }
    }


    updateStepIndicators(n, state.quickDocMaxStep, pipeline);
    
    
    if (n === 2) {
        resetQuickDocEditor();
        const empty = document.getElementById('q-preview-empty');
        const content = document.getElementById('q-rendered-content');
        const actions = document.getElementById('q-actions');
        const editBtn = document.getElementById('btn-q-edit');
        if (empty) empty.classList.remove('hidden');
        if (content) content.classList.add('hidden');
        if (actions) actions.classList.add('opacity-30', 'pointer-events-none');
        if (editBtn) editBtn.classList.add('hidden');
    }

    if(n === 3) {
        resetQuickDocEditor();
        const empty = document.getElementById('q-preview-empty');
        const content = document.getElementById('q-rendered-content');
        const actions = document.getElementById('q-actions');
        const editBtn = document.getElementById('btn-q-edit');
        if (empty) empty.classList.add('hidden');
        if (content) content.classList.remove('hidden');
        if (actions) actions.classList.remove('opacity-30', 'pointer-events-none');
        if (editBtn) {
            if (state.projectData.documentType === 'blog') editBtn.classList.remove('hidden');
            else editBtn.classList.add('hidden');
        }
        
        // Show/Hide Naver Blog publish button based on project documentType
        const naverBtn = document.getElementById('btn-q-naver-publish');
        if (naverBtn) {
            if (state.projectData.documentType === 'blog') {
                naverBtn.classList.remove('hidden');
            } else {
                naverBtn.classList.add('hidden');
            }
        }
        qUpdateLangs();
    }
    
    window.scrollTo(0, 0);
}

function qToggleAudience() {
    const select = document.getElementById('q-doc-audience');
    const custom = document.getElementById('q-custom-audience-field');
    if (!select || !custom) return;

    if (select.value === 'custom') custom.classList.remove('hidden');
    else custom.classList.add('hidden');
}

export function qUpdateLangs() {
    const checks = document.querySelectorAll('#q-lang-checkboxes input');
    if (checks.length === 0) return;

    let selectedLangs = Array.from(checks).filter(c => c.checked).map(c => c.value);
    if(selectedLangs.length === 0) { 
        checks[0].checked = true; 
        selectedLangs = [checks[0].value]; 
    }
    appState.setState({ selectedLangs });
    
    const ctn = document.getElementById('q-lang-tabs');
    if (!ctn) return;

    const activeLang = document.querySelector('#q-lang-tabs .q-lang-tab.active')?.dataset.lang;
    ctn.innerHTML = '';
    
    selectedLangs.forEach((l) => {
        const div = document.createElement('div');
        div.className = `q-lang-tab border-b-2 px-4 py-2 font-bold text-sm cursor-pointer hover:bg-slate-50 transition ${activeLang === l ? 'active border-teal-500 bg-white text-teal-600' : 'bg-slate-50 border-transparent text-slate-400'}`;
        div.innerText = l;
        div.dataset.lang = l;
        div.addEventListener('click', () => qSwitchLangTab(div, l));
        ctn.appendChild(div);
    });
    
    if (selectedLangs.length > 0) {
        // Try to keep active lang if possible, else pick first
        const targetLang = (activeLang && selectedLangs.includes(activeLang)) ? activeLang : selectedLangs[0];
        const tabEl = ctn.querySelector(`.q-lang-tab[data-lang="${targetLang}"]`);
        if (tabEl) qSwitchLangTab(tabEl, targetLang);
    }
}

function qSwitchLangTab(el, lang) {
    const editorWasOpen = !document.getElementById('q-editor-wrap')?.classList.contains('hidden');
    saveQuickDocEditorContent();

    document.querySelectorAll('#q-lang-tabs .q-lang-tab').forEach(t => {
        t.classList.remove('active', 'border-teal-500', 'bg-white', 'text-teal-600');
        t.classList.add('bg-slate-50', 'border-transparent', 'text-slate-400');
    });
    el.classList.remove('bg-slate-50', 'border-transparent', 'text-slate-400');
    el.classList.add('active', 'border-teal-500', 'bg-white', 'text-teal-600');
    
    const content = document.getElementById('q-rendered-content');
    const empty = document.getElementById('q-preview-empty');
    if (content) {
        const state = appState.getState();
        const generatedDocs = state.projectData.quickDocResults || {};
        if (generatedDocs[lang]) {
            content.innerHTML = renderQuickDocPreviewContent(generatedDocs[lang], lang);
            content.classList.remove('hidden');
            if (empty) empty.classList.add('hidden');
            if (editorWasOpen && isQuickDocBlogProject()) {
                setQuickDocEditMode(true, lang);
            }
        } else {
            resetQuickDocEditor();
            content.innerHTML = `<div class="flex flex-col items-center justify-center h-full text-slate-400">
                <div class="animate-pulse mb-2">[${lang}] 생성 대기 중...</div>
                <div class="text-xs">AI가 문서를 작성하고 있습니다. 잠시만 기다려 주세요.</div>
            </div>`;
            content.classList.remove('hidden');
            if (empty) empty.classList.add('hidden');
        }
    }
}

function showLoader(title, desc) {
    if(document.getElementById('loader-title')) document.getElementById('loader-title').innerText = title; 
    if(document.getElementById('loader-desc')) document.getElementById('loader-desc').innerText = desc; 
    if(document.getElementById('global-loader')) document.getElementById('global-loader').classList.remove('modal-hidden'); 
}
function hideLoader() {
    if(document.getElementById('global-loader')) document.getElementById('global-loader').classList.add('modal-hidden'); 
}

function stripAIResponseFences(response) {
    return String(response || "")
        .replace(/```json/gi, "")
        .replace(/```/g, "")
        .trim();
}

function normalizeQuickDocGeneratedText(value) {
    return String(value || '')
        .replace(/\\(?=[가-힣A-Za-z])/g, '')
        .trim();
}

function normalizeQuickDocParsedValue(value) {
    if (Array.isArray(value)) return value.map(item => normalizeQuickDocParsedValue(item));
    if (value && typeof value === 'object') {
        return Object.fromEntries(Object.entries(value).map(([key, val]) => [key, normalizeQuickDocParsedValue(val)]));
    }
    if (typeof value === 'string') return normalizeQuickDocGeneratedText(value);
    return value;
}

function escapeInvalidJsonBackslashes(jsonText) {
    return String(jsonText || '').replace(/\\(?!["\\/bfnrtu])/g, '\\\\');
}

function escapeControlCharactersInsideJsonStrings(jsonText) {
    let output = '';
    let inString = false;
    let escaped = false;

    for (const ch of String(jsonText || '')) {
        if (inString) {
            if (escaped) {
                output += ch;
                escaped = false;
                continue;
            }
            if (ch === '\\') {
                output += ch;
                escaped = true;
                continue;
            }
            if (ch === '"') {
                output += ch;
                inString = false;
                continue;
            }
            if (ch === '\n') {
                output += '\\n';
                continue;
            }
            if (ch === '\r') {
                output += '\\r';
                continue;
            }
            if (ch === '\t') {
                output += '\\t';
                continue;
            }
            output += ch;
            continue;
        }

        output += ch;
        if (ch === '"') inString = true;
    }

    return output;
}

function quoteUnquotedBlogBodyValues(jsonText) {
    return String(jsonText || '').replace(
        /("body"\s*:\s*)(?!["{\[])([\s\S]*?)(?=\n\s*}\s*,?\s*\n\s*(?:"[^"]+"\s*:|\}))/g,
        (_match, prefix, body) => `${prefix}${JSON.stringify(String(body || '').trim())}`
    );
}

function balanceJsonObjectCandidate(jsonText) {
    const input = String(jsonText || '').trim();
    let inString = false;
    let escaped = false;
    let depth = 0;

    for (const ch of input) {
        if (inString) {
            if (escaped) {
                escaped = false;
            } else if (ch === '\\') {
                escaped = true;
            } else if (ch === '"') {
                inString = false;
            }
            continue;
        }

        if (ch === '"') {
            inString = true;
        } else if (ch === '{') {
            depth += 1;
        } else if (ch === '}') {
            depth -= 1;
        }
    }

    if (depth > 0) return `${input}${'}'.repeat(depth)}`;
    return input;
}

function getLanguageHeadingAliases(lang = '') {
    const aliases = {
        '한국어': ['한국어', 'korean'],
        English: ['english', '영어'],
        '日本語': ['日本語', '일본어', 'japanese'],
        '中文': ['中文', '중국어', 'chinese']
    };
    return aliases[lang] || [lang];
}

function normalizeLanguageMarkerLine(line = '') {
    return String(line || '')
        .trim()
        .replace(/^#{1,6}\s*/, '')
        .replace(/^[-*+]\s+/, '')
        .replace(/^\d+[.)]\s*/, '')
        .replace(/\*\*/g, '')
        .replace(/[_`]/g, '')
        .replace(/[：:]\s*$/, '')
        .replace(/^\[(.+)\]$/, '$1')
        .replace(/\s+/g, ' ')
        .trim();
}

function detectLanguageMarker(line = '', targetLangs = []) {
    const normalized = normalizeLanguageMarkerLine(line).toLowerCase();
    if (!normalized) return '';

    for (const lang of targetLangs) {
        const aliases = getLanguageHeadingAliases(lang).map(alias => String(alias || '').toLowerCase());
        if (aliases.some(alias => normalized === alias || normalized === `${alias} 버전` || normalized === `${alias} version`)) {
            return lang;
        }
    }
    return '';
}

function parseMarkdownSectionsByLanguage(markdown = '', targetLangs = []) {
    const lines = String(markdown || '').split(/\r?\n/);
    const parsed = {};
    let currentLang = '';
    let buffer = [];

    const flush = () => {
        if (!currentLang) return;
        const section = normalizeQuickDocGeneratedText(buffer.join('\n'));
        if (section.length > 0) parsed[currentLang] = section;
    };

    for (const line of lines) {
        const nextLang = detectLanguageMarker(line, targetLangs);
        if (nextLang) {
            flush();
            currentLang = nextLang;
            buffer = [];
            continue;
        }
        if (currentLang) buffer.push(line);
    }

    flush();
    return parsed;
}

function parseMarkdownMultiLanguageResponse(markdown = '', targetLangs = []) {
    const source = stripAIResponseFences(markdown);
    const langs = Array.isArray(targetLangs) ? targetLangs : [];
    const parsed = parseMarkdownSectionsByLanguage(source, langs);

    if (Object.keys(parsed).length > 0) {
        return parsed;
    }

    if (langs.length === 1 && source.trim()) return { [langs[0]]: normalizeQuickDocGeneratedText(source) };
    return null;
}

export function parseQuickDocGenerationResponse(response, targetLangs) {
    const cleanResponse = stripAIResponseFences(response);
    const jsonStart = cleanResponse.indexOf('{');
    const jsonEnd = cleanResponse.lastIndexOf('}');
    const rawJsonCandidate = jsonStart !== -1 && jsonEnd !== -1
        ? cleanResponse.substring(jsonStart, jsonEnd + 1).trim()
        : cleanResponse;
    const jsonCandidate = balanceJsonObjectCandidate(rawJsonCandidate);

    try {
        return normalizeQuickDocParsedValue(JSON.parse(jsonCandidate));
    } catch (primaryError) {
        try {
            const repaired = JSON.parse(escapeInvalidJsonBackslashes(jsonCandidate));
            return normalizeQuickDocParsedValue(repaired);
        } catch (repairError) {}

        try {
            const repaired = JSON.parse(escapeInvalidJsonBackslashes(escapeControlCharactersInsideJsonStrings(jsonCandidate)));
            return normalizeQuickDocParsedValue(repaired);
        } catch (controlRepairError) {}

        try {
            const repaired = JSON.parse(escapeInvalidJsonBackslashes(escapeControlCharactersInsideJsonStrings(quoteUnquotedBlogBodyValues(jsonCandidate))));
            return normalizeQuickDocParsedValue(repaired);
        } catch (bodyRepairError) {}

        const langs = Array.isArray(targetLangs) ? targetLangs : [];
        if (langs.length === 1) {
            const lang = langs[0];
            const escapedLang = lang.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const langValueMatch = jsonCandidate.match(new RegExp(`^\\{\\s*"${escapedLang}"\\s*:\\s*"([\\s\\S]*)"\\s*\\}\\s*$`));
            if (langValueMatch && langValueMatch[1]) {
                return {
                    [lang]: normalizeQuickDocGeneratedText(langValueMatch[1]
                        .replace(/\\"/g, '"')
                        .replace(/\\n/g, '\n')
                        .replace(/\\r/g, '\r')
                        .replace(/\\t/g, '\t')
                    )
                };
            }

            const orphanMarkdown = cleanResponse
                .replace(/^\{\s*"[^"]+"\s*:\s*"?/, "")
                .replace(/"?\s*\}\s*$/, "")
                .trim();
            if (orphanMarkdown.length > 0) {
                return { [lang]: normalizeQuickDocGeneratedText(orphanMarkdown) };
            }
        }

        const markdownParsed = parseMarkdownMultiLanguageResponse(cleanResponse, langs);
        if (markdownParsed) return markdownParsed;

        throw primaryError;
    }
}

async function runQStep1() {
    const state = appState.getState();
    const docType = state.projectData.documentType || 'meeting';
    let textContent = document.getElementById('q-text-input')?.value.trim() || "";
    const rawUserText = textContent.trim();

    if (docType === 'blog') {
        renderYouTubeTranscriptStatus(null);
        const sourceKind = detectQuickDocSourceKind(rawUserText);
        const sourceUrl = extractFirstUrl(rawUserText);
        let checkedTypes = Array.from(document.querySelectorAll('input[name="q-blog-type"]:checked')).map(c => c.value);
        if (checkedTypes.length === 0) {
            const defaultType = document.querySelector('input[name="q-blog-type"][value="정보 제공형"]');
            if (defaultType) defaultType.checked = true;
            checkedTypes = ['정보 제공형'];
        }
        state.projectData.quickDocBlogTypes = checkedTypes;
        state.projectData.quickDocBlogCategory = inferBlogCategoryFromTypes(checkedTypes);
        state.projectData.quickDocSourceKind = sourceKind;
        state.projectData.quickDocSourceUrl = sourceUrl;
        state.projectData.quickDocTranscriptStatus = null;
        const keywordMatch = rawUserText.match(/(?:노출\s*키워드|키워드|target\s*keywords?)\s*[:：]\s*([^\n]+)/i);
        if (keywordMatch && keywordMatch[1]) {
            state.projectData.quickDocBlogKeywords = keywordMatch[1]
                .split(/[,，]/)
                .map(keyword => keyword.trim())
                .filter(Boolean);
        }
    } else if (docType === 'experience') {
        try {
            let localMatched = null;
            const resp = await fetch('/js/data/tourist_spots.json');
            if (resp.ok) {
                const contentType = resp.headers.get("content-type");
                if (contentType && contentType.includes("application/json")) {
                    const spotsData = await resp.json();
                    localMatched = spotsData.records.find(r => textContent.includes(r.관광지명));
                    if (localMatched) {
                        textContent += `\n\n[참고 공공 관광지 정보]\n- 관광지명: ${localMatched.관광지명}\n- 주소: ${localMatched.소재지도로명주소 || localMatched.소재지지번주소 || '정보 없음'}\n- 관광지 구분: ${localMatched.관광지구분 || '일반'}\n- 주요 편익시설: ${localMatched.공공편익시설정보 || '정보 없음'}\n- 관광지 소개: ${localMatched.관광지소개 || '정보 없음'}`;
                        console.log(`[QuickDoc] Matched local tourist spot: ${localMatched.관광지명}`);
                    }
                }
            }

            const queryCandidates = textContent.split(/[\s,.\r\n]+/).filter(w => w.length >= 2 && w.length <= 15);
            for (const cand of queryCandidates.slice(0, 3)) {
                if (localMatched && localMatched.관광지명.includes(cand)) continue;
                
                try {
                    const tourResp = await fetch(`/api/tour-proxy?keyword=${encodeURIComponent(cand)}`);
                    if (tourResp.ok) {
                        const tourData = await tourResp.json();
                        const items = tourData.response?.body?.items?.item;
                        const itemList = Array.isArray(items) ? items : (items ? [items] : []);
                        
                        const item = itemList.find(it => it.title && it.addr1);
                        if (item) {
                            textContent += `\n\n[한국관광공사 실시간 관광정보]\n- 명칭: ${item.title}\n- 주소: ${item.addr1} ${item.addr2 || ''}\n- 분류: ${item.cat3 || '관광지'}\n- 사진 URL: ${item.firstimage || item.firstimage2 || '제공 안 됨'}`;
                            console.log(`[QuickDoc] Matched KTO real-time tourist spot: ${item.title} for query "${cand}"`);
                            break;
                        }
                    }
                } catch (e) {
                    console.warn(`[QuickDoc] KTO API RAG failed for candidate "${cand}":`, e);
                }
            }
        } catch (err) {
            console.warn("Failed to load or parse tourist spots database:", err);
        }
    }

    if (!rawUserText || rawUserText.length < 5) {
        const message = docType === 'blog'
            ? "블로그 주제나 핵심 소재를 먼저 입력해주세요. 사진만으로 작성하려면 사진 업로드 후 추출된 설명을 확인해 주세요."
            : "원문 맥락 또는 필수 기획 정보를 입력해주세요.";
        alert(message);
        return;
    }

    if (docType === 'blog' && state.projectData.quickDocBlogTypes?.length > 0) {
        const sourceKindLabel = {
            youtube: 'YouTube URL',
            url: '웹 URL',
            memo: '직접 입력 메모'
        }[state.projectData.quickDocSourceKind || 'memo'];
        const sourceLines = [
            `[블로그 소스 유형: ${sourceKindLabel}]`,
            state.projectData.quickDocSourceUrl ? `[소스 URL: ${state.projectData.quickDocSourceUrl}]` : '',
            `[블로그 작성 방향: ${state.projectData.quickDocBlogTypes.join(', ')}]`
        ].filter(Boolean);

        if (state.projectData.quickDocSourceKind === 'youtube' && state.projectData.quickDocSourceUrl) {
            showLoader("YouTube 대사 추출", "영상 자막/대사를 가져와 블로그 원문 맥락에 추가하고 있습니다...");
            const transcriptResult = await fetchYouTubeTranscriptContext(state.projectData.quickDocSourceUrl);
            hideLoader();
            state.projectData.quickDocTranscriptStatus = {
                success: transcriptResult.success,
                error: transcriptResult.error || '',
                title: transcriptResult.title || '',
                language: transcriptResult.language || '',
                isAutoGenerated: Boolean(transcriptResult.isAutoGenerated),
                preview: transcriptResult.transcript ? transcriptResult.transcript.slice(0, 280) : ''
            };
            renderYouTubeTranscriptStatus(transcriptResult);

            if (!transcriptResult.success) {
                const userNotes = removeFirstUrl(rawUserText);
                if (userNotes.length < 30) {
                    appState.saveCurrentProject();
                    return;
                }

                state.projectData.quickDocSourceKind = 'memo';
                textContent = `[블로그 소스 유형: 직접 입력 메모]\n[블로그 작성 방향: ${state.projectData.quickDocBlogTypes.join(', ')}]\n[YouTube 대사 추출 실패: ${transcriptResult.error || 'unknown_error'} - 본문에 이 상태를 노출하지 말 것]\n\n[사용자 직접 메모]\n${userNotes}`;
            } else {
                textContent = `${sourceLines.join('\n')}\n\n${transcriptResult.context}\n\n[사용자 입력]\n${rawUserText}`;
            }
        } else {
            textContent = `${sourceLines.join('\n')}\n\n${rawUserText}`;
        }
    }
    
    if (textContent.includes("⚠️") || textContent.includes("(Parsing failed)")) {
        alert("파일 파싱 결과가 유효하지 않습니다. 유효한 텍스트를 입력하거나 업로드를 다시 진행해주세요.");
        return;
    }
    
    state.projectData.quickDocSource = textContent;
    
    // Set a default name if it's still the generic one
    if (state.projectData.name.includes("신규 빠른 문서") || state.projectData.name === "대시보드") {
        let lines = textContent.split('\n').map(l => l.trim()).filter(l => l.length > 0);
        
        // 1. Look for H1
        let cleanTitle = lines.find(l => l.startsWith('# '))?.replace('# ', '').trim();
        
        // 2. Look for Labeled titles
        if (!cleanTitle) {
            let labeled = lines.find(l => /^(제목|Title|Subject|주제|Project):\s*/i.test(l));
            if (labeled) cleanTitle = labeled.replace(/^(제목|Title|Subject|주제|Project):\s*/i, '').trim();
        }

        // 3. Look for the first meaningful long line
        if (!cleanTitle) {
            cleanTitle = lines.find(l => l.length > 5 && !l.startsWith('[Source:') && !l.startsWith('---'));
        }

        if (!cleanTitle) cleanTitle = "빠른 문서";
        
        // Clean up markdown, special chars, and common prefixes
        cleanTitle = cleanTitle.replace(/[#*`\[\]()]/g, '').trim();
        if (cleanTitle.length > 40) cleanTitle = cleanTitle.substring(0, 37) + '...';
        
        const now = new Date();
        const dateStr = `${now.getFullYear()}${String(now.getMonth()+1).padStart(2,'0')}${String(now.getDate()).padStart(2,'0')}`;
        const timeStr = `${String(now.getHours()).padStart(2,'0')}${String(now.getMinutes()).padStart(2,'0')}`;
        
        state.projectData.name = `${cleanTitle}_${dateStr}_${timeStr}`;
        const titleInput = document.getElementById('project-title-input');
        if(titleInput) titleInput.value = state.projectData.name;
    }
    
    appState.saveCurrentProject();
    updateProjectDropdown();

    appState.setState({ quickDocMaxStep: Math.max(state.quickDocMaxStep, 2) });
    qStep(2);
}

async function runQStep2() {
    const state = appState.getState();
    const docType = document.getElementById('q-doc-type')?.value;
    const docTone = document.getElementById('q-doc-tone')?.value;
    let docAudience = document.getElementById('q-doc-audience')?.value;
    if (docAudience === 'custom') {
        docAudience = document.getElementById('q-custom-audience')?.value;
    }
    
    qUpdateLangs();
    const targetLangs = state.selectedLangs || ['한국어'];
    
    // Save config for persistence
    state.projectData.quickDocConfig = {
        docType: docType,
        docTone: docTone,
        docAudience: docAudience,
        selectedLangs: targetLangs
    };
    
    state.projectData.quickDocResults = {}; // reset previous
    
    appState.setState({ quickDocMaxStep: Math.max(state.quickDocMaxStep, 3) });
    qStep(3);
    
    const preview = document.getElementById('q-source-preview');
    if (preview) {
        preview.innerText = state.projectData.quickDocSource || "";
    }
    
    const pane = document.getElementById('q-rendered-content');
    showLoader("AI 문서 렌더링", `다국어(${targetLangs.join(', ')}) 생성 중...`);
    const adminState = getAdminState();
    const q1Prompt = adminState.prompts?.PROMPT_QUICKDOC_GEN || "당신은 전문 문서 작성 보조 AI입니다.";

    try {
        const isBlogDoc = state.projectData.documentType === 'blog' || docType === '블로그';
        const blogSourceKind = state.projectData.quickDocSourceKind || detectQuickDocSourceKind(state.projectData.quickDocSource);
        const blogSourceGuide = {
            youtube: '- 입력 소스에는 성공적으로 추출된 YouTube 자막/대사 원문이 포함되어 있습니다. 대사에서 핵심 메시지, 사례, 흐름을 뽑아 블로그 글로 재구성하십시오.',
            url: '- 입력 소스가 웹 URL입니다. URL과 사용자가 입력한 메모를 핵심 참고 자료로 삼아 작성하십시오.',
            memo: '- 입력 소스가 사용자의 직접 메모입니다. 메모에서 핵심 주제, 독자, 검색 의도를 추출하십시오.'
        }[blogSourceKind] || '';
        const outputShape = isBlogDoc
            ? `{
  ${targetLangs.map(l => `"${l}": {"title": "SEO 제목", "description": "150자 이내 설명", "tags": ["태그1", "태그2", "태그3", "태그4"], "body": "문서 내용..."}`).join(',\n  ')}
}`
            : `{
  ${targetLangs.map(l => `"${l}": "문서 내용..."`).join(',\n  ')}
}`;
        const combinedPrompt = `${q1Prompt}

[원본 맥락]: 
${state.projectData.quickDocSource}

[작성 조건]
- 문서 종류: ${docType}
- 타겟 독자: ${docAudience}
- 어조: ${docTone}
- 요청 언어: ${targetLangs.join(', ')}
${isBlogDoc ? `- 블로그 소스 유형: ${blogSourceKind}\n${blogSourceGuide}` : ''}

[출력 지침]
- 위 조건에 맞춰 요청된 모든 언어로 각각 문서를 작성하십시오.
- 반드시 아래의 JSON 형식을 엄격히 준수하여 하나의 JSON 객체로만 응답하십시오.
- JSON 문자열 안의 줄바꿈은 반드시 \\n으로 이스케이프하십시오.
- Markdown 수식($)이나 LaTeX 기호는 절대 사용하지 마십시오.
${isBlogDoc ? `- 블로그 문서는 각 언어별로 title, description, tags, body를 반드시 생성하십시오.
- tags는 검색 노출에 유효한 핵심 태그 3-5개를 배열로 생성하십시오.
- body는 제목, 소제목, 목록, 독자 행동 유도 문장을 포함한 마크다운 본문이어야 합니다.
- "작성 조건", "출력 지침", "자동 추출 상태", 오류 코드, URL 자체를 본문 소재로 쓰지 마십시오.
- YouTube 자막/대사 추출 실패 메시지가 입력에 있으면 본문에 노출하지 말고 사용자가 직접 입력한 메모만 바탕으로 작성하십시오.
- YouTube 대사가 제공된 경우 대사를 그대로 나열하지 말고, 핵심 주장 3-5개를 뽑아 독자가 읽을 수 있는 블로그 글로 재구성하십시오.
- 도입부는 영상/메모의 문제의식으로 시작하고, 본문은 소제목별 인사이트와 실천 포인트를 포함하십시오.
- 결론에는 독자가 바로 적용할 수 있는 체크리스트 또는 다음 행동을 포함하십시오.` : ''}

${outputShape}

[주의] JSON 외의 인사말이나 부연 설명은 절대 포함하지 마십시오.`;

        const result = await aiService.callAI(combinedPrompt, "", `PROMPT_QUICKDOC_GEN_MULTI`); 
        if (!result) throw new Error("AI 응답을 받지 못했습니다.");
        
        let parsedResults = {};
        try {
            parsedResults = parseQuickDocGenerationResponse(result, targetLangs);
        } catch (e) {
            console.error("AI JSON parsing failed. Raw response:", result);
            console.error(e);
            throw new Error("AI가 유효한 형식으로 문서를 생성하지 못했습니다. (형식 오류)");
        }

        // Map results with smart key matching
        const blogMetadata = isBlogDoc ? extractQuickDocBlogMetadata(parsedResults, targetLangs) : null;
        const finalResults = {};
        targetLangs.forEach(lang => {
            let matchedValue = parsedResults[lang];
            if (!matchedValue) {
                const matchedKey = Object.keys(parsedResults).find(k => 
                    k.trim().toLowerCase() === lang.trim().toLowerCase()
                );
                if (matchedKey) matchedValue = parsedResults[matchedKey];
            }
            if (!matchedValue && targetLangs.length === 1 && Object.keys(parsedResults).length === 1) {
                matchedValue = Object.values(parsedResults)[0];
            }
            finalResults[lang] = matchedValue ? normalizeQuickDocGeneratedValue(matchedValue) : `⚠️ [${lang}] 생성 유실됨`;
        });
        
        // Update state via state manager to ensure all listeners/views are notified
        const freshState = appState.getState();
        appState.setState({ 
            projectData: { 
                ...freshState.projectData, 
                quickDocResults: finalResults,
                ...(blogMetadata ? {
                    quickDocBlogTitle: blogMetadata.title,
                    quickDocBlogDescription: blogMetadata.description,
                    quickDocBlogGeneratedTags: blogMetadata.tags,
                    quickDocBlogKeywords: blogMetadata.tags.length > 0 ? blogMetadata.tags : (freshState.projectData.quickDocBlogKeywords || [])
                } : {})
            } 
        });

        if (blogMetadata?.tags?.length) {
            const keywordInput = document.getElementById('q-blog-target-keywords');
            if (keywordInput) keywordInput.value = blogMetadata.tags.join(', ');
        }
        
        // Enable actions
        const actions = document.getElementById('q-actions');
        if (actions) {
            actions.classList.remove('opacity-30', 'pointer-events-none');
        }
        
        // Save and notify
        appState.saveCurrentProject();
        
        // Force UI update
        setTimeout(() => {
            qUpdateLangs();
        }, 100);
        
    } catch(e) {
        alert("AI 생성 실패: " + e.message);
    } finally {
        hideLoader();
    }
}

export function initQuickDocUI() {
    document.getElementById('btn-q-step-1')?.addEventListener('click', () => qStep(1));
    document.getElementById('btn-q-step-2')?.addEventListener('click', () => qStep(2));
    document.getElementById('btn-q-step-3')?.addEventListener('click', () => qStep(3));
    
    document.getElementById('btn-next-q2')?.addEventListener('click', runQStep1);
    document.getElementById('btn-run-quickdoc')?.addEventListener('click', runQStep2);

    // Bind Naver Blog API Modal events
    document.getElementById('btn-naver-api-toggle-edit')?.addEventListener('click', openNaverApiModal);
    document.getElementById('btn-close-naver-api-modal')?.addEventListener('click', closeNaverApiModal);
    document.getElementById('btn-naver-api-cancel')?.addEventListener('click', closeNaverApiModal);
    document.getElementById('btn-naver-api-save')?.addEventListener('click', saveNaverApiConfig);
    
    updateNaverApiStatusUI();

    document.getElementById('q-doc-audience')?.addEventListener('change', qToggleAudience);

    const checks = document.querySelectorAll('#q-lang-checkboxes input');
    checks.forEach(c => c.addEventListener('change', qUpdateLangs));

    document.getElementById('btn-q-edit')?.addEventListener('click', () => {
        const editorWrap = document.getElementById('q-editor-wrap');
        setQuickDocEditMode(editorWrap?.classList.contains('hidden'));
    });
    
    populateLanguageCheckboxes();

    // File Upload handling
    const fileUpload = document.getElementById('q-file-upload');
    if (fileUpload) {
        fileUpload.addEventListener('change', async (e) => {
            const files = e.target.files;
            if (files.length === 0) return;
            showLoader("파일 파싱 중", "텍스트를 추출하고 있습니다.");
            try {
                const extractedTexts = await FileParser.parseFiles(Array.from(files));
                const state = appState.getState();
                const docType = state.projectData.documentType || 'meeting';
                
                const textArea = document.getElementById('q-text-input');
                if (textArea) {
                    textArea.value = (textArea.value ? textArea.value + "\n\n" : "") + extractedTexts;
                    textArea.classList.add('pulse-light');
                    setTimeout(() => textArea.classList.remove('pulse-light'), 2000);
                }
            } catch (err) {
                console.error(err);
                alert("파일 읽기 실패");
            } finally {
                hideLoader();
            }
        });
    }

    document.getElementById('btn-q-copy')?.addEventListener('click', () => {
        saveQuickDocEditorContent();
        const activeTab = document.querySelector('.q-lang-tab.active');
        const activeLang = activeTab?.dataset.lang;
        const text = getQuickDocResultText(activeLang);
        
        console.log(`[QuickDoc] Copy attempt. Lang: ${activeLang}, Found: ${!!text}`);
        
        if (text) {
            navigator.clipboard.writeText(text).then(() => alert("클립보드에 복사되었습니다.")).catch(err => alert("복사 실패: " + err));
        } else {
            alert("복사할 내용이 없습니다. 먼저 문서를 생성해 주세요.");
        }
    });

    document.getElementById('btn-q-naver-publish')?.addEventListener('click', publishToNaverBlog);

    document.getElementById('btn-q-download')?.addEventListener('click', () => {
        saveQuickDocEditorContent();
        const activeTab = document.querySelector('.q-lang-tab.active');
        const activeLang = activeTab?.dataset.lang;
        const text = getQuickDocResultText(activeLang);
        
        console.log(`[QuickDoc] Download attempt. Lang: ${activeLang}, Found: ${!!text}`);

        if (text) {
            const blob = new Blob([text], { type: 'text/markdown;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `AuraDoc_${activeLang}_${Date.now()}.md`;
            a.click();
            URL.revokeObjectURL(url);
        } else {
            alert("다운로드할 내용이 없습니다. 먼저 문서를 생성해 주세요.");
        }
    });

    loadQuickDocDropdowns();
    window.addEventListener('adminStateChanged', loadQuickDocDropdowns);
}

function loadQuickDocDropdowns() {
    const state = getAdminState();
        
        const typeSelect = document.getElementById('q-doc-type');
        if (typeSelect) {
            typeSelect.innerHTML = state.documentTypes.map(d => `<option value="${d.name}">${d.name}</option>`).join('');
            
            // Re-apply locked and synced documentType immediately after rebuilding options
            const appStateData = appState.getState();
            if (appStateData.projectData) {
                const docTypeNameMap = {
                    'meeting': '회의록',
                    'email': '이메일',
                    'blog': '블로그',
                    'experience': '체험학습'
                };
                const targetVal = appStateData.projectData.quickDocType || docTypeNameMap[appStateData.projectData.documentType] || '회의록';
                typeSelect.value = targetVal;
                typeSelect.disabled = true;
                typeSelect.classList.add('bg-slate-100', 'cursor-not-allowed', 'text-slate-500', 'opacity-80');
            }
        }
        
        const toneSelect = document.getElementById('q-doc-tone');
        if (toneSelect) {
            toneSelect.innerHTML = state.tones.map(t => `<option value="${t.name}">${t.name}</option>`).join('');
        }
        
        const audSelect = document.getElementById('q-doc-audience');
        if (audSelect) {
            audSelect.innerHTML = state.audiences.map(a => `<option value="${a.name}">${a.name}</option>`).join('') + `<option value="custom">✏️ 직접 입력</option>`;
        }
}

function populateLanguageCheckboxes() {
    const container = document.getElementById('q-lang-checkboxes');
    if (!container) return;
    
    const langs = [
        { id: 'ko', name: '한국어' },
        { id: 'en', name: 'English' },
        { id: 'ja', name: '日本語' },
        { id: 'zh', name: '中文' }
    ];
    
    container.innerHTML = langs.map(l => `
        <label class="custom-checkbox bg-white border border-gray-100 p-3 rounded-xl hover:bg-slate-50 transition shadow-sm">
            <input type="checkbox" value="${l.name}" ${l.id === 'ko' ? 'checked' : ''}>
            <span class="checkmark"></span>
            <span class="text-sm font-bold text-slate-700">${l.name}</span>
        </label>
    `).join('');
    
    // Bind listeners to new checkboxes
    container.querySelectorAll('input').forEach(c => {
        c.addEventListener('change', qUpdateLangs);
    });
}

function restoreQuickDocConfig() {
    const state = appState.getState();
    const config = state.projectData?.quickDocConfig;
    if (!config) return;

    console.log("[QuickDoc] Restoring config:", config);

    // 1. Dropdowns
    const typeSelect = document.getElementById('q-doc-type');
    if (typeSelect) {
        const docTypeNameMap = {
            'meeting': '회의록',
            'email': '이메일',
            'blog': '블로그',
            'experience': '체험학습'
        };
        const targetVal = state.projectData?.quickDocType || docTypeNameMap[state.projectData?.documentType] || '회의록';
        typeSelect.value = targetVal;
        typeSelect.disabled = true;
        typeSelect.classList.add('bg-slate-100', 'cursor-not-allowed', 'text-slate-500', 'opacity-80');
    }

    const toneSelect = document.getElementById('q-doc-tone');
    if (toneSelect && config.docTone) toneSelect.value = config.docTone;

    const audSelect = document.getElementById('q-doc-audience');
    if (audSelect && config.docAudience) {
        // Check if it's a default option or custom
        const exists = Array.from(audSelect.options).some(opt => opt.value === config.docAudience);
        if (exists) {
            audSelect.value = config.docAudience;
        } else {
            audSelect.value = 'custom';
            const customInput = document.getElementById('q-custom-audience');
            if (customInput) customInput.value = config.docAudience;
        }
    }

    // 2. Languages
    if (config.selectedLangs) {
        const checkboxes = document.querySelectorAll('#q-lang-checkboxes input[type="checkbox"]');
        checkboxes.forEach(cb => {
            cb.checked = config.selectedLangs.includes(cb.value);
        });
        // Sync state.selectedLangs
        state.selectedLangs = [...config.selectedLangs];
    }
    
    // Update tabs if in step 3
    if (state.quickDocStep === 3) {
        qUpdateLangs();
    }
}

export async function updateNaverApiStatusUI() {
    const badge = document.getElementById('naver-api-status-badge');
    const textStatus = document.getElementById('naver-api-display-status');
    if (!badge || !textStatus) return;

    try {
        const config = await naverBlogService.getConfig();
        if (config && config.naverId && config.clientId) {
            badge.innerText = "🟢 연동 완료";
            badge.className = "mt-1 px-1.5 py-0.5 rounded-full text-[8px] font-black bg-emerald-50 text-emerald-600 border border-emerald-100";
            textStatus.innerHTML = `<strong>연동 ID:</strong> ${config.naverId}<br><span class="text-[9px] text-slate-400">Client ID: ${config.clientId.substring(0, 4)}***</span>`;
        } else {
            badge.innerText = "🔌 미연동";
            badge.className = "mt-1 px-1.5 py-0.5 rounded-full text-[8px] font-black bg-rose-50 text-rose-500 border border-rose-100";
            textStatus.innerText = "등록된 API 정보가 없습니다. 클릭하여 연동을 완료해 주세요.";
        }
    } catch (e) {
        console.error("Failed to parse naver blog config:", e);
    }
}

export async function openNaverApiModal() {
    const modal = document.getElementById('naver-blog-api-modal');
    if (!modal) return;
    
    const config = await naverBlogService.getConfig();
    const idInput = document.getElementById('naver-modal-id');
    const clientInput = document.getElementById('naver-modal-client-id');
    const secretInput = document.getElementById('naver-modal-client-secret');

    if (idInput) idInput.value = config?.naverId || '';
    if (clientInput) clientInput.value = config?.clientId || '';
    if (secretInput) secretInput.value = config?.clientSecret || '';

    modal.classList.remove('modal-hidden');
}

export function closeNaverApiModal() {
    const modal = document.getElementById('naver-blog-api-modal');
    if (modal) modal.classList.add('modal-hidden');
}

export async function saveNaverApiConfig() {
    const idInput = document.getElementById('naver-modal-id');
    const clientInput = document.getElementById('naver-modal-client-id');
    const secretInput = document.getElementById('naver-modal-client-secret');

    const naverId = idInput?.value?.trim();
    const clientId = clientInput?.value?.trim();
    const clientSecret = secretInput?.value?.trim();

    if (!naverId || !clientId || !clientSecret) {
        alert("모든 설정값(ID, Client ID, Client Secret)을 입력해 주세요.");
        return;
    }

    const config = { naverId, clientId, clientSecret };
    await naverBlogService.saveConfig(config);
    
    closeNaverApiModal();
    await updateNaverApiStatusUI();
    alert("네이버 블로그 API 연동 정보가 Firestore DB 및 로컬 저장소에 안전하게 저장되었습니다.");
}

async function publishToNaverBlog() {
    saveQuickDocEditorContent();
    const config = await naverBlogService.getConfig();

    if (!config || !config.naverId || !config.clientId || !config.clientSecret) {
        alert("네이버 블로그 API 연동 설정 정보가 없습니다. 먼저 API 정보를 입력해 주세요.");
        await openNaverApiModal();
        return;
    }

    const activeTab = document.querySelector('.q-lang-tab.active');
    const activeLang = activeTab?.dataset.lang;
    const state = appState.getState();
    const textContent = getQuickDocResultText(activeLang);

    if (!textContent) {
        alert("발행할 블로그 본문 내용이 존재하지 않습니다.");
        return;
    }

    let extractedTitle = state.projectData?.quickDocBlogTitle || 'AuraDoc AI 블로그 포스트';
    const h1Match = textContent.match(/^#\s+(.+)$/m);
    if (!state.projectData?.quickDocBlogTitle && h1Match && h1Match[1]) {
        extractedTitle = h1Match[1].trim();
    } else if (!state.projectData?.quickDocBlogTitle) {
        const lines = textContent.split('\n').map(l => l.trim()).filter(l => l.length > 0);
        if (lines.length > 0) {
            extractedTitle = lines[0].replace(/[#*`_]/g, '');
        }
    }

    if (typeof window.showLoader === 'function') {
        window.showLoader("네이버 발행 중", "블로그 API 프록시를 통해 포스팅을 발행하고 있습니다...");
    }

    try {
        const keywordsInput = document.getElementById('q-blog-target-keywords');
        const savedKeywords = [
            ...(state.projectData?.quickDocBlogGeneratedTags || []),
            ...(state.projectData?.quickDocBlogKeywords || [])
        ].filter(Boolean);
        const tags = savedKeywords.length > 0
            ? Array.from(new Set(savedKeywords.map(tag => String(tag).trim()).filter(Boolean)))
            : (keywordsInput?.value ? keywordsInput.value.split(',').map(t => t.trim()).filter(Boolean) : []);

        const response = await fetch('/api/naver-publish', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                title: extractedTitle,
                content: textContent,
                naverId: config.naverId,
                clientId: config.clientId,
                clientSecret: config.clientSecret,
                tags: tags
            })
        });

        const resData = await response.json();
        
        if (typeof window.hideLoader === 'function') {
            window.hideLoader();
        }

        if (response.ok && resData.success) {
            alert(`🎉 [네이버 블로그 발행 완료]\n\n게시글이 성공적으로 네이버 블로그에 발행되었습니다!\n\n확인을 누르면 발행된 글로 즉시 이동합니다.`);
            window.open(resData.publishUrl, '_blank');
        } else {
            alert(`❌ 발행 실패: ${resData.message || 'API 서버 오류가 발생했습니다.'}`);
        }
    } catch (err) {
        if (typeof window.hideLoader === 'function') {
            window.hideLoader();
        }
        console.error("Naver publish failure:", err);
        alert("네이버 블로그 포스팅 중 오류 발생: " + err.message);
    }
}

window.updateNaverApiStatusUI = updateNaverApiStatusUI;
window.openNaverApiModal = openNaverApiModal;
window.closeNaverApiModal = closeNaverApiModal;
window.saveNaverApiConfig = saveNaverApiConfig;
